import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import ResponsivePopover from "../Popover/ResponsivePopover";
import IconButton, { TOUCH_TARGET } from "../Button/IconButton/IconButton";
import FilledButton from "../Button/FilledButton/FilledButton";
import OutlinedButton from "../Button/OutlinedButton/OutlinedButton";
import Avatar, { type AvatarRingStatus } from "../Avatar/Avatar";
import FontText from "../FontText/FontText";
import { FontSizeEnum, FontTypeEnum } from "../FontText/FontTextEnums";
import { ThemeColorEnum, ThemeIconEnum } from "../Themes/ThemeEnums";
import { ITheme } from "../Themes/ThemeTypes";
import { logout } from "../../../Auth/logout";
import { useResource } from "../../../Resources/useResource";
import { useRacerProfileSubtitle } from "../../../Api/useRacerProfileSubtitle";
import { ProfileMenuResources } from "../../../Resources/ProfileMenuResources";
import { PicoComRoutes } from "../../../Routes/PicoComRoutes";
import { LanguageEnum } from "jadro-pico/enums/common/LanguageEnum";
import type { AppAuthState } from "../../../Redux/AppAuthReducer";
import {
  OperationStatusEnum,
  operationsClearResolvedAction,
  type AppOperationsState,
  type OperationEntry,
} from "../../../Redux/AppOperationsReducer";

const TriggerSlot = styled.div`
  width: ${TOUCH_TARGET}px;
  height: ${TOUCH_TARGET}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const CloseRow = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CloseButtonSlot = styled.div`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
`;

const Identity = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;
`;

const Headline = styled.div<{ theme: ITheme }>`
  text-align: center;
  font-size: ${(props) => props.theme.base.typography.title.medium.font_size}px;
  line-height: ${(props) => props.theme.base.typography.title.medium.line_height}px;
  font-weight: ${(props) => props.theme.base.typography.title.medium.font_weight};
`;

const SupportingText = styled.div<{ theme: ITheme }>`
  font-size: ${(props) => props.theme.base.typography.label.small.font_size}px;
  line-height: ${(props) => props.theme.base.typography.label.small.line_height}px;
  color: rgb(${(props) => props.theme.colors.on_surface_variant});
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

const OperationsList = styled.div<{ theme: ITheme }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid rgba(${(props) => props.theme.colors.on_surface}, 0.12);
`;

const OperationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const statusColor: Record<OperationStatusEnum, ThemeColorEnum> = {
  [OperationStatusEnum.PENDING]: ThemeColorEnum.ON_SURFACE_VARIANT,
  [OperationStatusEnum.COMPLETED]: ThemeColorEnum.PRIMARY,
  [OperationStatusEnum.FAILED]: ThemeColorEnum.ERROR,
};

const statusLabel = (
  status: OperationStatusEnum,
  resources: { operationPending: string; operationCompleted: string; operationFailed: string },
): string => {
  switch (status) {
    case OperationStatusEnum.PENDING:
      return resources.operationPending;
    case OperationStatusEnum.COMPLETED:
      return resources.operationCompleted;
    case OperationStatusEnum.FAILED:
      return resources.operationFailed;
  }
};

const ProfileMenu: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector((s: { auth: AppAuthState }) => s.auth.user);
  const language = useSelector(
    (s: { core: { language?: LanguageEnum } }) => s.core.language ?? LanguageEnum.CS,
  );
  const operations = useSelector(
    (s: { operations: AppOperationsState }) => s.operations.operations,
  );
  const resources = useResource(ProfileMenuResources);
  const subtitle = useRacerProfileSubtitle();

  useEffect(() => {
    dispatch(operationsClearResolvedAction());
  }, [dispatch]);

  const ringStatus: AvatarRingStatus | undefined = operations.some(
    (o) => o.status === OperationStatusEnum.FAILED,
  )
    ? "failed"
    : operations.some((o) => o.status === OperationStatusEnum.PENDING)
      ? "pending"
      : undefined;

  if (!user) return null;

  const displayName = user.name || user.preferred_username || user.email || "";

  const manageAccountUrl = `${import.meta.env.VITE_PICO_COM_FE_URL}${PicoComRoutes.PROFILE.paths[language]}?returnTo=${encodeURIComponent(window.location.href)}`;

  return (
    <ResponsivePopover
      surfaceColor={ThemeColorEnum.SURFACE_CONTAINER}
      desktopBorderRadius={12}
      desktopMinWidth="280px"
      onOpenChange={(isOpen) => {
        if (!isOpen) dispatch(operationsClearResolvedAction());
      }}
      renderTrigger={({ toggle }) => (
        <TriggerSlot>
          <Avatar
            name={displayName}
            size={40}
            onClick={toggle}
            ariaLabel={resources.profileAriaLabel}
            ringStatus={ringStatus}
          />
        </TriggerSlot>
      )}
    >
      {({ close }) => (
        <>
          <CloseRow>
            <Headline>{displayName || resources.profileAriaLabel}</Headline>
            <CloseButtonSlot>
              <IconButton
                icon={ThemeIconEnum.CLOSE}
                onClick={close}
                ariaLabel={resources.closeAriaLabel}
              />
            </CloseButtonSlot>
          </CloseRow>
          <Identity>
            <Avatar name={displayName} size={64} />
            {subtitle && <SupportingText>{subtitle}</SupportingText>}
          </Identity>
          {operations.length > 0 && (
            <OperationsList>
              <SupportingText>{resources.operationsTitle}</SupportingText>
              {operations.map((operation: OperationEntry) => (
                <OperationRow key={operation.operationUuid}>
                  <FontText
                    text={operation.label}
                    $fontType={FontTypeEnum.BODY}
                    $fontSize={FontSizeEnum.MEDIUM}
                    $color={ThemeColorEnum.ON_SURFACE}
                  />
                  <FontText
                    text={statusLabel(operation.status, resources)}
                    $fontType={FontTypeEnum.LABEL}
                    $fontSize={FontSizeEnum.SMALL}
                    $color={statusColor[operation.status]}
                  />
                </OperationRow>
              ))}
            </OperationsList>
          )}
          <Actions>
            <OutlinedButton
              label={resources.manageAccount}
              onClick={() => {
                close();
                window.location.href = manageAccountUrl;
              }}
            />
            <FilledButton
              label={resources.logoutButton}
              icon={ThemeIconEnum.LOGOUT}
              onClick={() => {
                close();
                void logout();
              }}
            />
          </Actions>
        </>
      )}
    </ResponsivePopover>
  );
};

export default ProfileMenu;
